import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfigChange, ConfigPart, getPathLabel, groupByPart, isApplicable, isPreselected } from './changes.ts';
import ChangeValue from './ChangeValue.tsx';
import Button from '../../../core/components/Button.tsx';
import Checkbox from '../../../core/components/Checkbox.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import classnames from '../../../core/utils/classnames.tsx';

const WarningIcon = withIcon(faTriangleExclamation);

interface ChangeSelection {
    selected: Set<string>;
    setSelected: (paths: string[], isSelected: boolean) => void;
}

const useChangeSelection = (changes: ConfigChange[]): ChangeSelection => {
    const [picks, setPicks] = useState<Record<string, boolean>>({});
    const selected = new Set(
        changes.filter((change) => picks[change.path] ?? isPreselected(change)).map((change) => change.path)
    );
    const setSelected = (paths: string[], isSelected: boolean) =>
        setPicks((current) => ({ ...current, ...Object.fromEntries(paths.map((path) => [path, isSelected])) }));
    return { selected, setSelected };
};

interface ChangeNoteProps {
    change: ConfigChange;
}

const ChangeNote = ({ change }: ChangeNoteProps) => {
    const { t } = useTranslation('config');
    if (!isApplicable(change)) {
        return <span className="text-xs text-content-medium">{t('configPage.history.change.staysInChannel')}</span>;
    }
    if (change.kind === 'RISKY') {
        return (
            <span className="flex items-start gap-2 text-xs text-content-warning-strong">
                <WarningIcon className="mt-0.5 flex-none" />
                {t('configPage.history.change.risky')}
            </span>
        );
    }
    return null;
};

interface ChangeCardsProps {
    parts: ConfigPart[];
    changes: ConfigChange[];
    selection?: ChangeSelection;
    empty: string;
}

interface PartCheckboxProps {
    changes: ConfigChange[];
    selection: ChangeSelection;
}

// risky changes are ticked one by one, so the part's checkbox only ticks normal changes but unticks them all
const PartCheckbox = ({ changes, selection }: PartCheckboxProps) => {
    const applicablePaths = changes.filter(isApplicable).map((change) => change.path);
    const normalPaths = changes.filter(isPreselected).map((change) => change.path);
    const selectedCount = applicablePaths.filter((path) => selection.selected.has(path)).length;
    const isNormalSelected = normalPaths.length > 0 && normalPaths.every((path) => selection.selected.has(path));
    return (
        <Checkbox
            checked={selectedCount > 0 && selectedCount === applicablePaths.length}
            indeterminate={selectedCount > 0 && selectedCount < applicablePaths.length}
            disabled={normalPaths.length === 0 && selectedCount === 0}
            onChange={() =>
                normalPaths.length > 0 && !isNormalSelected
                    ? selection.setSelected(normalPaths, true)
                    : selection.setSelected(applicablePaths, false)
            }
        />
    );
};

interface ChangeRowProps {
    change: ConfigChange;
    selection?: ChangeSelection;
}

const ChangeRow = ({ change, selection }: ChangeRowProps) => {
    const { t } = useTranslation('config');
    const isSelectable = Boolean(selection) && isApplicable(change);
    const Row = isSelectable ? 'label' : 'div';
    return (
        <Row
            className={classnames(
                'flex items-start gap-3 border-0 border-t border-solid border-line-weak px-4 py-3',
                isSelectable && 'cursor-pointer'
            )}
        >
            {selection && (
                <Checkbox
                    className="mt-0.5"
                    checked={isSelectable && selection.selected.has(change.path)}
                    disabled={!isSelectable}
                    onChange={() => selection.setSelected([change.path], !selection.selected.has(change.path))}
                />
            )}
            <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span
                    className={classnames(
                        'text-xs',
                        isApplicable(change) ? 'text-content-medium' : 'text-content-weak'
                    )}
                >
                    {getPathLabel(change, t)}
                </span>
                <ChangeValue change={change} />
                {selection && <ChangeNote change={change} />}
            </span>
        </Row>
    );
};

const ChangeCards = ({ parts, changes, selection, empty }: ChangeCardsProps) => {
    const { t } = useTranslation('config');
    const groups = groupByPart(parts, changes);
    if (groups.length === 0) {
        return (
            <div className="rounded-lg border border-solid border-line-weak px-4 py-6 text-center text-sm text-content-medium">
                {empty}
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-3">
            {groups.map(({ part, changes: partChanges }) => (
                <div
                    key={part.option}
                    className="flex flex-col overflow-hidden rounded-lg border border-solid border-line-weak bg-surface-weak"
                >
                    <div className="flex items-center gap-3 bg-surface-medium px-4 py-2">
                        {selection && <PartCheckbox changes={partChanges} selection={selection} />}
                        <span className="min-w-0 flex-1 text-sm font-semibold text-content-strong">{part.label}</span>
                        <span className="flex-none text-xs text-content-medium">
                            {t('configPage.history.changeCount', { count: partChanges.length })}
                        </span>
                    </div>
                    {partChanges.map((change) => (
                        <ChangeRow key={change.path} change={change} selection={selection} />
                    ))}
                </div>
            ))}
        </div>
    );
};

interface ChangeReviewModalProps {
    title: string;
    description: React.ReactNode;
    parts: ConfigPart[];
    changes: ConfigChange[];
    status?: React.ReactNode;
    empty: string;
    submitKey: string;
    onSubmit: (paths: string[]) => Promise<string>;
    onClose: () => void;
}

export const ChangeReviewModal = ({
    title,
    description,
    parts,
    changes,
    status = undefined,
    empty,
    submitKey,
    onSubmit,
    onClose,
}: ChangeReviewModalProps) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();
    const selection = useChangeSelection(changes);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const count = selection.selected.size;

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            publishToast({ description: await onSubmit([...selection.selected]), status: 'success' });
            onClose();
        } catch (error) {
            console.error(error);
            publishToast({ description: t('configPage.history.error'), status: 'error' });
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            title={title}
            onClose={onClose}
            preventCloseOnOverlayClick
            buttons={
                <>
                    <Button onClick={onClose}>{t('configPage.history.cancel')}</Button>
                    <Button
                        variant="primary"
                        disabled={Boolean(status) || count === 0}
                        loading={isSubmitting}
                        onClick={handleSubmit}
                    >
                        {t(submitKey, { count })}
                    </Button>
                </>
            }
        >
            <div className="flex w-[40rem] max-w-full flex-col gap-4 pb-2">
                <span className="text-sm text-content-medium">{description}</span>
                {status ?? <ChangeCards parts={parts} changes={changes} selection={selection} empty={empty} />}
            </div>
        </Modal>
    );
};

export default ChangeCards;
